import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import {
  Award,
  Target,
  Users,
  Globe,
  ArrowRight,
  Leaf,
  FlaskConical,
  Sprout,
  ShieldCheck,
  Quote
} from 'lucide-react';

export const AboutPage = () => {
  const { t } = useTranslation();
  const labImage = "https://images.unsplash.com/photo-1580982327559-c1202864eb05?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MDV8MHwxfHNlYXJjaHwxfHxhZ3JpY3VsdHVyYWwlMjBzY2llbnRpc3QlMjBsYWJvcmF0b3J5fGVufDB8fHx8MTc3MTI4MzAwMXww&ixlib=rb-4.1.0&q=85";
  const soilImage = "https://images.unsplash.com/photo-1683366091055-3dba3d814f4f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAxODF8MHwxfHNlYXJjaHwxfHxwaWxlJTIwb2YlMjByaWNoJTIwc29pbCUyMGNvbXBvc3R8ZW58MHx8fHwxNzcxMjgzMDEyfDA&ixlib=rb-4.1.0&q=85";
  const fieldImage = "https://images.unsplash.com/photo-1765570265263-edda751afcd9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzV8MHwxfHNlYXJjaHwxfHxsdXNoJTIwZ3JlZW4lMjBhZ3JpY3VsdHVyZSUyMGZpZWxkJTIwY2xvc2UlMjB1cHxlbnwwfHx8fDE3NzEyODMwMDB8MA&ixlib=rb-4.1.0&q=85";

  const values = [
    {
      icon: <FlaskConical className="w-6 h-6" />,
      title: t('about.val1_title'),
      description: t('about.val1_desc'),
    },
    {
      icon: <Sprout className="w-6 h-6" />,
      title: t('about.val2_title'),
      description: t('about.val2_desc'),
    },
    {
      icon: <Users className="w-6 h-6" />,
      title: t('about.val3_title'),
      description: t('about.val3_desc'),
    },
    {
      icon: <ShieldCheck className="w-6 h-6" />,
      title: t('about.val4_title'),
      description: t('about.val4_desc'),
    },
  ];

  const stats = [
    { value: '38+', label: t('about.stats.products') },
    { value: '4', label: t('about.stats.categories') },
    { value: 'ISO', label: t('about.stats.iso') },
    { value: '100%', label: t('about.stats.quality') },
  ];

  return (
    <div data-testid="about-page" className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="py-20 md:py-32 bg-gradient-to-b from-emerald-50 to-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div>
              <span className="text-sm uppercase tracking-widest font-semibold text-emerald-600 mb-4 block">
                {t('about.title_span')}
              </span>
              <h1
                data-testid="about-title"
                className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 font-['Outfit'] tracking-tight leading-tight mb-6"
              >
                {t('about.title_main')}<br />
                <span className="text-emerald-700">{t('about.title_highlight')}</span>
              </h1>
              <p className="text-lg text-slate-600 mb-8 leading-relaxed">
                {t('about.description')}
              </p>

              <div className="flex items-center gap-4 p-4 bg-amber-50 rounded-2xl border border-amber-100">
                <div className="w-14 h-14 bg-amber-500 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Award className="w-7 h-7 text-white" />
                </div>
                <div>
                  <p className="font-semibold text-slate-900">{t('about.iso_title')}</p>
                  <p className="text-sm text-slate-600">{t('about.iso_desc')}</p>
                </div>
              </div>
            </div>

            <div className="relative">
              <div className="grid grid-cols-2 gap-4">
                <img
                  src={labImage}
                  alt="Research Laboratory"
                  className="w-full h-64 object-cover rounded-2xl"
                />
                <img
                  src={soilImage}
                  alt="Rich Soil"
                  className="w-full h-64 object-cover rounded-2xl mt-8"
                />
              </div>
              <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 bg-emerald-900 text-white px-6 py-3 rounded-full text-sm font-medium whitespace-nowrap">
                {t('about.img_cap')}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-emerald-900">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <p className="text-4xl md:text-5xl font-bold text-white font-['Outfit'] mb-2">
                  {stat.value}
                </p>
                <p className="text-emerald-300 font-medium">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Founder's Note Section */}
      <section className="py-20 md:py-32 bg-gradient-to-b from-emerald-50 to-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="text-sm uppercase tracking-widest font-semibold text-emerald-600 mb-4 block">
              {t('about.founder_section')}
            </span>
            <h2
              data-testid="founder-title"
              className="text-3xl md:text-5xl font-semibold text-slate-900 font-['Outfit'] tracking-tight"
            >
              {t('about.founder_title')}
            </h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 items-center">
            {/* Founder Image */}
            <div className="lg:col-span-2">
              <div className="relative">
                <div className="aspect-[4/5] rounded-3xl overflow-hidden shadow-2xl">
                  <img
                    src="/founder.png"
                    alt="Nimmala Ravinder Reddy - Founder"
                    data-testid="founder-image"
                    className="w-full h-full object-cover"
                  />
                </div>
                {/* Decorative elements */}
                <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-emerald-900 rounded-2xl -z-10" />
                <div className="absolute -top-6 -left-6 w-24 h-24 bg-amber-500 rounded-2xl -z-10" />
              </div>
            </div>

            {/* Founder Info */}
            <div className="lg:col-span-3 space-y-6">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-100 rounded-full">
                <Quote className="w-4 h-4 text-emerald-700" />
                <span className="text-sm font-medium text-emerald-800">{t('about.founder_badge')}</span>
              </div>

              <h3
                data-testid="founder-name"
                className="text-2xl md:text-3xl font-bold text-slate-900 font-['Outfit']"
              >
                {t('about.founder_name')}
              </h3>

              <p className="text-amber-600 font-semibold">
                {t('about.founder_role')}
              </p>

              <div className="space-y-4 text-slate-600 leading-relaxed">
                <p>
                  {t('about.founder_p1')}
                </p>

                <p>
                  {t('about.founder_p2_1')}<strong className="text-slate-900">{t('about.founder_p2_2')}</strong>{t('about.founder_p2_3')}
                </p>

                <p>
                  {t('about.founder_p3')}
                </p>

                <p className="text-emerald-800 font-medium italic border-l-4 border-emerald-500 pl-4">
                  {t('about.founder_quote')}
                </p>
              </div>

              <div className="flex flex-wrap gap-4 pt-4">
                <div className="flex items-center gap-2 px-4 py-2 bg-slate-100 rounded-lg">
                  <Award className="w-5 h-5 text-amber-600" />
                  <span className="text-sm font-medium text-slate-700">{t('about.founder_badge1')}</span>
                </div>
                <div className="flex items-center gap-2 px-4 py-2 bg-slate-100 rounded-lg">
                  <Sprout className="w-5 h-5 text-emerald-600" />
                  <span className="text-sm font-medium text-slate-700">{t('about.founder_badge2')}</span>
                </div>
                <div className="flex items-center gap-2 px-4 py-2 bg-slate-100 rounded-lg">
                  <ShieldCheck className="w-5 h-5 text-blue-600" />
                  <span className="text-sm font-medium text-slate-700">ISO 9001:2015</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <Card className="border-emerald-100 bg-gradient-to-br from-emerald-50 to-white overflow-hidden">
              <CardContent className="p-8 md:p-12">
                <div className="w-16 h-16 bg-emerald-900 rounded-2xl flex items-center justify-center mb-6">
                  <Target className="w-8 h-8 text-white" />
                </div>
                <h3
                  data-testid="mission-title"
                  className="text-2xl md:text-3xl font-semibold text-slate-900 font-['Outfit'] mb-4"
                >
                  {t('about.mission_title')}
                </h3>
                <p className="text-slate-600 leading-relaxed">
                  {t('about.mission_desc')}
                </p>
              </CardContent>
            </Card>

            <Card className="border-amber-100 bg-gradient-to-br from-amber-50 to-white overflow-hidden">
              <CardContent className="p-8 md:p-12">
                <div className="w-16 h-16 bg-amber-500 rounded-2xl flex items-center justify-center mb-6">
                  <Globe className="w-8 h-8 text-white" />
                </div>
                <h3
                  data-testid="vision-title"
                  className="text-2xl md:text-3xl font-semibold text-slate-900 font-['Outfit'] mb-4"
                >
                  {t('about.vision_title')}
                </h3>
                <p className="text-slate-600 leading-relaxed">
                  {t('about.vision_desc')}
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="py-20 md:py-32 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="text-sm uppercase tracking-widest font-semibold text-emerald-600 mb-4 block">
              {t('about.values_section')}
            </span>
            <h2
              data-testid="values-title"
              className="text-3xl md:text-5xl font-semibold text-slate-900 font-['Outfit'] tracking-tight"
            >
              {t('about.values_title')}
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => (
              <Card
                key={index}
                data-testid={`value-card-${index}`}
                className="border-emerald-100/50 bg-white hover:shadow-lg transition-all duration-300"
              >
                <CardContent className="p-6">
                  <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center text-emerald-700 mb-4">
                    {value.icon}
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900 font-['Outfit'] mb-2">
                    {value.title}
                  </h3>
                  <p className="text-sm text-slate-600 leading-relaxed">
                    {value.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Image Section */}
      <section className="py-20 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="relative rounded-3xl overflow-hidden">
            <img
              src={fieldImage}
              alt="Lush Green Fields"
              className="w-full h-[400px] md:h-[500px] object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-emerald-900/80 to-emerald-900/40" />

            <div className="absolute inset-0 flex items-center">
              <div className="max-w-xl px-8 md:px-16">
                <h2 className="text-3xl md:text-4xl font-semibold text-white font-['Outfit'] mb-4">
                  {t('about.img2_title')}
                </h2>
                <p className="text-emerald-100 mb-8 leading-relaxed">
                  {t('about.img2_desc')}
                </p>
                <Link to="/products">
                  <Button
                    data-testid="explore-products-btn"
                    className="bg-amber-500 hover:bg-amber-600 text-white rounded-full px-8 py-6 text-lg font-semibold"
                  >
                    {t('about.explore')}
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-emerald-50">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <div className="w-16 h-16 bg-emerald-900 rounded-2xl flex items-center justify-center mx-auto mb-6">
            <Leaf className="w-8 h-8 text-emerald-400" />
          </div>
          <h2
            data-testid="about-cta-title"
            className="text-3xl md:text-4xl font-semibold text-slate-900 font-['Outfit'] mb-4"
          >
            {t('about.cta_title')}
          </h2>
          <p className="text-lg text-slate-600 mb-8 max-w-2xl mx-auto">
            {t('about.cta_desc')}
          </p>
          <Link to="/contact">
            <Button
              data-testid="about-contact-btn"
              className="bg-emerald-900 hover:bg-emerald-800 text-white rounded-full px-8 py-6 text-lg font-semibold btn-shadow"
            >
              {t('about.contact_btn')}
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};
